'use client';

import { CircularProgress } from '@mui/material';
import React, { LicenseInfo } from '@mui/x-license';
import {
	Authenticator,
	CustomCookieStorage,
	cognitoUserPoolsTokenProvider,
	configureAmplify,
	handleSignIn,
	SSOLogin,
} from '@parkchirp/api-client';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import AppContextProvider from '~/contexts/app.context';

import '@parkchirp/ui-library/src/styles/index.scss';
import '../styles/UniversalPaymentSearch.scss';
import '~/styles/index.scss';
import 'common-ui-library/src/styles/globalStyles.scss';

import '../styles/richTextEditor.css';
import { amplifyConfig } from '../constants';
import { getSharedCookieDomain } from '../utils/getSharedCookieDomain';
import { type AuthResult, setCookies } from '../utils/SSO/setCookies';

configureAmplify(amplifyConfig, false);
const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
const currentHostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
const cookieDomain = getSharedCookieDomain(currentHostname, process.env.NEXT_PUBLIC_MONTHLY_URL);
const setSSOCookies = (authResult: AuthResult, hostname: string, protocol: string) =>
	setCookies(authResult, hostname, protocol, cookieDomain);
cognitoUserPoolsTokenProvider.setKeyValueStorage(new CustomCookieStorage({ secure: !!isHttps, domain: cookieDomain }));
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_LICENSE as string);

const MyApp = ({ Component, pageProps }: AppProps) => {
	const [cookies] = useCookies();
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isSSO, setIsSSO] = useState(false);
	const router = useRouter();

	// Handle SSO Login
	const handleSSOLogin = useCallback(async () => {
		if (!router.query?.code || isSSO) return;

		try {
			setIsSSO(true);
			await SSOLogin(
				router.query.code as string,
				process.env.NEXT_PUBLIC_AWS_USER_POOL_ID as string,
				process.env.NEXT_PUBLIC_AWS_APP_CLIENT_ID as string,
				setSSOCookies,
				process.env.NEXT_PUBLIC_COGNITO_DOMAIN as string,
				process.env.NEXT_PUBLIC_COGNITO_REDIRECT_URI as string,
			);
			setIsLoggedIn(true);
			setLoading(false);
			window.location.href = '/control-panel';
		} catch (error) {
			console.error('Error in SSOLogin:', error);
		}
	}, [router, isSSO]);

	// Handle Regular Login
	const handleRegularLogin = useCallback(async () => {
		if (!Object.keys(cookies).length || isLoggedIn || isSSO) return;

		try {
			await handleSignIn(
				cookies,
				process.env.NEXT_PUBLIC_AWS_USER_POOL_ID as string,
				process.env.NEXT_PUBLIC_AWS_APP_CLIENT_ID as string,
			);
			setIsLoggedIn(true);
			setLoading(false);
		} catch (error) {
			console.error('Error in handleSignIn:', error);
		}
	}, [cookies, isLoggedIn, isSSO]);

	useEffect(() => {
		if (!router.isReady) return;

		if (router.query?.code) {
			handleSSOLogin();
		} else {
			handleRegularLogin();
		}
	}, [router.isReady, router.query?.code, handleSSOLogin, handleRegularLogin]);

	if (loading && isLoggedIn) {
		return (
			<div
				style={{
					width: '100%',
					height: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<CircularProgress />
			</div>
		);
	}

	return (
		<Authenticator
			loginMechanisms={['email']}
			signUpAttributes={['given_name', 'family_name']}
			passwordSettings={{
				minLength: 12,
				requireSpecialCharacters: false,
			}}
		>
			<AppContextProvider pageProps={pageProps} Component={Component} />
		</Authenticator>
	);
};

export default MyApp;
